import { useAuthActions } from '@convex-dev/auth/react'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { revalidateLogic } from '@tanstack/react-form'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { IconEyeOutline18, IconEyeSlashOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AuthLayout } from '~/components/auth/auth-layout'
import { GoogleButton } from '~/components/auth/google-button'
import { Button } from '~/components/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/components/ui/field'
import { Input } from '~/components/ui/input'
import { Spinner } from '~/components/ui/spinner'
import { makeSignInSchema } from '~/lib/auth'
import { useAppForm } from '~/lib/form'
import { titleFor } from '~/lib/route-title'
import { ensureAuth } from '~/lib/router-auth'
import { usePostAuthNavigation } from '~/lib/use-post-auth-navigation'
import { convexErrorCode } from '~convex/lib/errorCodes'

// Only follow in-app paths, and never bounce back to an auth screen.
const sanitizeRedirect = (value: unknown): string => {
  if (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/sign-')
  ) {
    return value
  }
  return '/home'
}

export const Route = createFileRoute('/sign-in')({
  beforeLoad: async ({ search }) => {
    const auth = await ensureAuth()
    if (auth.isAuthenticated) {
      throw redirect({ to: sanitizeRedirect(search.redirect) })
    }
  },
  component: RouteComponent,
  // robots.txt already disallows /sign-in, but defensive `noindex` catches
  // crawlers that ignore robots.txt and render JS.
  head: () => ({
    meta: [{ title: titleFor(msg`Sign in`) }, { content: 'noindex, nofollow', name: 'robots' }],
  }),
  // Client-only: auth runs against the Convex client, no SSR needed.
  ssr: false,
  // `redirect` carries the destination an unauthenticated visitor was sent
  // away from, so sign-in can return them to it.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
})

function RouteComponent() {
  const { t, i18n } = useLingui()
  const { signIn } = useAuthActions()
  const { redirect: rawRedirect } = Route.useSearch()
  const redirectTo = sanitizeRedirect(rawRedirect)
  const { beginPostAuthNavigation, isAwaitingAuthentication } = usePostAuthNavigation(redirectTo)
  const [showPassword, setShowPassword] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const schema = useMemo(() => makeSignInSchema(i18n), [i18n])

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await signIn('password', {
          // Lower-cased so the account matches regardless of how the email
          // was capitalised at sign-up.
          email: value.email.trim().toLowerCase(),
          flow: 'signIn',
          password: value.password,
        })
        if (!result.signingIn) {
          throw new Error('Password sign-in did not create a session')
        }
        beginPostAuthNavigation()
      } catch (error) {
        // A closed account must not be told its password is wrong: that
        // sends the one person who knows better to the reset form, where
        // nothing works either.
        if (convexErrorCode(error) === 'ACCOUNT_DELETED') {
          toast.error(
            t`This account was deleted. Write to contato@planno.app if you'd like it back.`
          )
          return
        }
        console.error('Sign-in error:', error)
        toast.error(
          navigator.onLine
            ? t`Incorrect email or password`
            : t`You're offline. Check your connection and try again.`
        )
      }
    },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
  })

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      await signIn('google', { redirectTo })
    } catch (error) {
      console.error('Google sign-in error:', error)
      toast.error(
        navigator.onLine
          ? t`Could not sign in with Google`
          : t`You're offline. Check your connection and try again.`
      )
      // On success the page redirects away, so only re-enable on failure.
      setGoogleLoading(false)
    }
  }

  return (
    <AuthLayout
      altPrompt={
        <Trans>
          Don't have an account?{' '}
          <Link
            className="font-medium text-foreground underline-offset-4 transition-colors hover:underline"
            to="/sign-up"
          >
            Sign up
          </Link>
        </Trans>
      }
      googleButton={
        <GoogleButton
          disabled={googleLoading}
          label={t`Continue with Google`}
          onClick={handleGoogleSignIn}
        />
      }
      subtitle={<Trans>Sign in to continue</Trans>}
      title={<Trans>Welcome back</Trans>}
    >
      <form
        className="animate-fade-up space-y-6"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        style={{ animationDelay: '0ms' }}
      >
        <FieldGroup>
          {/* Email field */}
          <form.Field name="email">
            {(field) => (
              <Field>
                <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                  <Trans>Email</Trans>
                </FieldLabel>
                <Input
                  aria-describedby={`${field.name}-error`}
                  aria-invalid={field.state.meta.errors.length > 0}
                  autoComplete="email"
                  className="h-11 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                  id={field.name}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t`you@email.com`}
                  type="email"
                  value={field.state.value}
                />
                <FieldError
                  className="text-xs"
                  errors={field.state.meta.errors}
                  id={`${field.name}-error`}
                />
              </Field>
            )}
          </form.Field>

          {/* Password field */}
          <form.Field name="password">
            {(field) => (
              <Field>
                <div className="flex items-center justify-between">
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Password</Trans>
                  </FieldLabel>
                  <form.Subscribe selector={(state) => state.values.email}>
                    {(email) => (
                      <Link
                        className="text-muted-foreground text-xs underline-offset-4 transition-colors hover:text-foreground hover:underline"
                        search={email.trim() ? { email: email.trim() } : {}}
                        to="/forgot-password"
                      >
                        <Trans>Forgot password?</Trans>
                      </Link>
                    )}
                  </form.Subscribe>
                </div>
                <div className="relative">
                  <Input
                    aria-describedby={`${field.name}-error`}
                    aria-invalid={field.state.meta.errors.length > 0}
                    autoComplete="current-password"
                    className="h-11 pr-10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                    id={field.name}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="••••••••"
                    type={showPassword ? 'text' : 'password'}
                    value={field.state.value}
                  />
                  <Button
                    aria-label={showPassword ? t`Hide password` : t`Show password`}
                    className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    {showPassword ? (
                      <IconEyeSlashOutline18 className="size-5" />
                    ) : (
                      <IconEyeOutline18 className="size-5" />
                    )}
                  </Button>
                </div>
                <FieldError
                  className="text-xs"
                  errors={field.state.meta.errors}
                  id={`${field.name}-error`}
                />
              </Field>
            )}
          </form.Field>

          {/* Submit button */}
          <form.Subscribe>
            {(state) => (
              <Field className="pt-2">
                <Button
                  className="h-11 w-full font-medium transition-all"
                  disabled={!state.canSubmit || state.isSubmitting || isAwaitingAuthentication}
                  type="submit"
                >
                  {state.isSubmitting || isAwaitingAuthentication ? (
                    <span className="flex items-center gap-2">
                      <Spinner className="size-4 text-current" />
                      <Trans>Signing in...</Trans>
                    </span>
                  ) : (
                    <Trans>Sign in</Trans>
                  )}
                </Button>
              </Field>
            )}
          </form.Subscribe>
        </FieldGroup>
      </form>
    </AuthLayout>
  )
}
